import { ChangeDetectionStrategy, Component, computed, effect, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, signal, untracked, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { ConfigurationManagerService, ScreenOrientationService } from 'src/app/core';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { Categories, Departments } from 'tilby-models';
import { ResizableDirective } from 'src/app/directives/resizable.directive';
import { convertVwToPx } from 'src/app/shared/utils';
import { ParamsInputOperationsQuickFilter, ParamsOutputOperations, QuickFiltersComponent } from './quick-filters/quick-filters.component';
import { CustomFiltersComponent, ParamsInputOperationsCustomFilter, ParamsOutputOperationsCustomFilter } from './custom-filters/custom-filters.component';
import { TranslateModule } from '@ngx-translate/core';
import { mobileCheck } from 'src/utilities';

export interface FilterOption {
  name: string;
  id?: number | string | undefined;
}

export interface Filter {
  name: string;
  field: string; //keyof ItemFe;
  filter: any,
  filterParams: any,
  options: FilterOption[];
}

export interface FilterPreference {
  name: string;
  filterValues: any;
}

export interface TransformedActiveFiltersInterface {
  name: string; 
  values: string[];
  filter: any; 
  filterParams: any;
}

export interface FilterModelMulti {
  [index: number]: FilterModel
}

export interface FilterModel {
  filterType: 'set' | 'multi';
  values?: (string | null)[];
  filterModels?: FilterModelMulti[];
}

export interface AgGridFilterModelInterface {
  [filterName: string]: FilterModel;
}

export interface ApplyGridFilterEvent {
  agGridFilters: AgGridFilterModelInterface;
  activeFilterType: ExtendedTableTab;
  customFilterNameSelected?: string;
  customFilterNameEdited?: string;
}

export type ExtendedTableTab = "quickFilters" | "customFilters";

const COMPACT_THRESHOLD = 1300;
const MIN_WIDTH_SIDENAV = 300;
const MAX_WIDTH_SIDENAV = 450;
const START_WIDTH_SIDENAV = 380;

@Component({
  selector: 'app-extended-table',
  standalone: true,
  imports: [ MatIconModule, CommonModule,
      MatSidenavModule, MatButtonModule,
      MatSelectModule, MatFormFieldModule,
      MatExpansionModule, MatCheckboxModule,
      MatInputModule, ReactiveFormsModule,
      MatTabsModule, MatBadgeModule,
      ResizableDirective,
      QuickFiltersComponent,
      CustomFiltersComponent,
      TranslateModule
  ],
  templateUrl: './extended-table.component.html',
  styleUrl: './extended-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush 
})
export class ExtendedTableComponent implements OnInit, OnDestroy {

  isSidenavExpanded: WritableSignal<boolean> = signal(false);
  quickFiltersOperation: WritableSignal<ParamsInputOperationsQuickFilter> = signal({operation: ''});
  customFiltersOperation: WritableSignal<ParamsInputOperationsCustomFilter> = signal({operation: ''});
  filterApplied: WritableSignal<boolean> = signal(false);

  // private readonly media = inject(MediaMatcher);
  // private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly configurationManager= inject(ConfigurationManagerService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);

  private readonly categories = this.itemDataCatalogService.getExtendedCategories();
  private readonly departments = this.itemDataCatalogService.getDataByKey("departments");
  private readonly screenOrientationService = inject(ScreenOrientationService);

  protected isMobilePortrait = this.screenOrientationService.isMobilePortrait;
  
  @Input() hideResetFilterIcon: boolean = false;
  @Input() activeTabs = new Map<ExtendedTableTab, Filter[]>();
  @Output() applyGridFilters = new EventEmitter<ApplyGridFilterEvent | null>();
  @Output() sidebarResized = new EventEmitter<void>();

  @Input() set customAgGridFilters(value: ApplyGridFilterEvent | null) {
    this._customAgGridFilters = value;
    this.initFilters(value);
  }
  get customAgGridFilters(): ApplyGridFilterEvent | null {
    return this._customAgGridFilters;
  }
  private _customAgGridFilters: ApplyGridFilterEvent | null = null;

  @Input() set isSidebarVisible(visible: boolean) {
    this._isSidebarVisible.set(visible);
    if (!visible) {
      this.isSidenavExpanded.set(false);
    }
  }
  get isSidebarVisible(): boolean {
    return this._isSidebarVisible();
  }
  private _isSidebarVisible = signal(true);

  updateAgGridFilters(value: ApplyGridFilterEvent | null) {
    this._customAgGridFilters = value;
  }

  activeFilters: TransformedActiveFiltersInterface[] = [];
  agGridFilterModel: AgGridFilterModelInterface = {};

  initialQuickFilters: AgGridFilterModelInterface | null = null;
  initialCustomFilters: ApplyGridFilterEvent | null = null;
  selectedTabIndex: number = 0;
  protected isMobile = mobileCheck();
  private lastDeviceIsMobile = this.isMobile;

  isSidebarReady = signal(false);
  sidebarWidth = signal(60);

  private minWidth = computed(() => {
    if (!this.isSidenavExpanded()) return 60;
    return this.isMobilePortrait() ? convertVwToPx(90) - 80 : 250;
  });

  private maxWidth = computed(() => {
    void this.isMobilePortrait();
    return convertVwToPx(90) - 50;
  });

  private windowWidth = signal(window.innerWidth);

  protected isCompactMode = computed(() => {
    return this.isMobile || this.windowWidth() < COMPACT_THRESHOLD;
  });

  protected sidenavMode = computed(() => {
    return this.isCompactMode() ? 'over' : 'side';
  });

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event) {
    const currentWidth = window.innerWidth;
    const currentCategory = mobileCheck();

    if (currentCategory !== this.lastDeviceIsMobile) {
      this.lastDeviceIsMobile = currentCategory;
    }

    this.windowWidth.set(currentWidth);
  }

  onResize(newWidth: number = 0): void {
    if (!newWidth) return;

    const currentCategory = mobileCheck();
    if (currentCategory !== this.lastDeviceIsMobile) {
      this.lastDeviceIsMobile = currentCategory;
      return; 
    }

    const widthToProcess = newWidth || this.sidebarWidth();
  
    const min = this.minWidth();
    const max = this.maxWidth();

    if (widthToProcess <= min) {
      this.sidebarWidth.set(min);
    } else if (widthToProcess >= max) {
      this.sidebarWidth.set(max);
    } else {
      this.sidebarWidth.set(widthToProcess);
    }

    this.sidebarResized.emit();
  }

  constructor() {
    this.isSidenavExpanded.set(this.configurationManager.getPreference("grid.items.show_filters") ?? false);

    effect(() => {
      void this.windowWidth();
      void this.isMobilePortrait();
      void this.isSidenavExpanded();

      untracked(() => {
        this.applyResponsiveWidth();
      });
    }, { allowSignalWrites: true });
  }

  private applyResponsiveWidth() {
    if (!this.isSidenavExpanded()) {
        this.sidebarWidth.set(60);
        return;
    }

    const w = this.windowWidth();
    let targetWidth: number;

    if (this.isMobilePortrait()) {
        targetWidth = this.maxWidth(); 
    } 
    else if (this.isCompactMode()) {
        const idealWidth = w * 0.4; 
        targetWidth = Math.max(MIN_WIDTH_SIDENAV, Math.min(idealWidth, MAX_WIDTH_SIDENAV));
    } 
    else {
      targetWidth = START_WIDTH_SIDENAV;
    }

    this.sidebarWidth.set(targetWidth);
  }

  getFilters(key: ExtendedTableTab): Filter[] {
    return this.activeTabs.get(key) ?? [];
  }

  ngOnInit(): void { }

  initFilters(data: ApplyGridFilterEvent | null) {
    if (data) {
      if (data.activeFilterType === 'quickFilters') {
        this.initialQuickFilters = data.agGridFilters;
        this.selectedTabIndex = 0;
      } else if (data.activeFilterType === 'customFilters') {
        this.initialCustomFilters = data;
        this.selectedTabIndex = 1;
      }
      this.filterApplied.set(data.agGridFilters && !!Object.keys(data.agGridFilters)?.length);
    } else {
      // Reset o stato pulito se data è null
      this.initialQuickFilters = null;
      this.initialCustomFilters = null;
      this.filterApplied.set(false);
    }

    if(!this.isSidebarReady()) this.isSidebarReady.set(true);
  }

  handleQuickFiltersOperation(event: ParamsOutputOperations) {
    if(event) {
      if(event.operation === 'applyFilters') {
        this.applyFilters('quickFilters', event.extras?.formValue);
      } else if(event.operation === 'toggleSidenav') {
        this.toggleSidenav();
      } else if(event.operation === 'reset'){
        this.resetFilters(event.extras?.deepReset);
      }
    }
  }

  handleCustomFiltersOperation(event: ParamsOutputOperationsCustomFilter) {
    if(event) {
      if(event.operation === 'applyFilters') {
        this.applyFilters('customFilters', event.extras?.formValue, event.extras?.filterName);
      } else if(event.operation === 'applySavedFilter') {
        this.applySavedFilter(event.extras?.filter, event.extras?.updateOnlyIcon);
      } else if(event.operation === 'startSavingFilter') {
        this.startSavingFilter('customFilters', event.extras?.formValue);
      } else if(event.operation === 'toggleSidenav') {
        this.toggleSidenav();
      // } else if(event.operation === 'resetFilteredResult') {
      //   this.resetFilteredResult();
      } else if(event.operation === 'reset'){
        this.resetFilters(event.extras?.deepReset);
      }
    }
  }

  resetFilters(deepReset = false) {
    this.activeFilters = [];
    this.agGridFilterModel = {};
    this.filterApplied.set(false);

    if(deepReset) {
      this.resetFilteredResult();
    }
  }

  resetFilteredResult() {
    this.updateSelectValueOnTable(null);
    this.applyGridFilters.emit(null);
  }

  public resetFiltersFromExternal() {
    if(this.selectedTabIndex === 0) {
      this.quickFiltersOperation.set({operation: "resetOnlyFilters"})
    } else {
      this.customFiltersOperation.set({operation: "resetOnlyFilters"})
    }
  }

  public initFiltersFromExternal(filterStatus: ApplyGridFilterEvent) {
    if(this.selectedTabIndex === 0) {
      this.quickFiltersOperation.set({operation: "initFilters", extras: { filterStatus: filterStatus?.agGridFilters}})
      this.updateAgGridFilters(filterStatus);
      this.initialQuickFilters = filterStatus?.agGridFilters;
    } else {
      this.resetFiltersFromExternal();
      this.updateAgGridFilters(null);
      this.initialCustomFilters = null;
    }
  }

  public updateFilterStatus(filterStatus: ApplyGridFilterEvent) {
    this.updateAgGridFilters(filterStatus);

    const sidebarFields = [
      ...this.getFilters('quickFilters').map(f => f.field)
    ];

    const activeFilterKeys = filterStatus?.agGridFilters 
      ? Object.keys(filterStatus.agGridFilters) 
      : [];

    const hasActiveSidebarFilter = activeFilterKeys.some(key => sidebarFields.includes(key));
    this.filterApplied.set(hasActiveSidebarFilter);

    if(this.selectedTabIndex === 0) {
      this.initialQuickFilters = filterStatus?.agGridFilters;
      this.initialCustomFilters = null;
    } else {
      this.initialCustomFilters = filterStatus;
      this.initialQuickFilters = null;
    }
  }

  tabPanelChange($event: MatTabChangeEvent) {
    if (!this.isSidenavExpanded()) return;

    if ($event.index === this.selectedTabIndex) return;

    this.selectedTabIndex = $event.index;

    if($event.index === 0) {
      this.customFiltersOperation.set({operation: "reset", extras: {deepReset: true}});
    } else {
      this.quickFiltersOperation.set({operation: "reset", extras: {deepReset: true}});
    }
  }

  toggleSidenav(): void {
    this.isSidenavExpanded.update(value => !value);
    this.configurationManager.setUserPreference("grid.items.show_filters", this.isSidenavExpanded())

    this.sidebarResized.emit();
  }

  prepareFilter(key: ExtendedTableTab, formValue: any) {
    this.activeFilters = this.transformFilters(key, formValue);
    this.agGridFilterModel = this.getGridFilters();
  }

  applyFilters(key: ExtendedTableTab, formValue: any, filterName = undefined): void {
    this.prepareFilter(key, formValue);
    this.updateSelectValueOnTable(this.agGridFilterModel);
    this.applyGridFilters.emit({
      agGridFilters: this.agGridFilterModel,
      activeFilterType: key,
      customFilterNameSelected: filterName
    });
    this.filterApplied.set(Object.keys(this.agGridFilterModel).length > 0);
  }

  applySavedFilter(filter: any, updateOnlyIcon: boolean): void {
    this.filterApplied.set(!!filter);
    if(!updateOnlyIcon) {
      this.updateSelectValueOnTable(filter?.[0].filterValues);
      this.applyGridFilters.emit({
        agGridFilters: filter?.[0].filterValues,
        activeFilterType: 'customFilters',
        customFilterNameSelected: filter?.[0].name
      });
    }
  }

  startSavingFilter(key: ExtendedTableTab, formValue: any) {
    this.prepareFilter(key, formValue);
    this.customFiltersOperation.set({operation: 'finishSavingFilter', extras: {filter: this.agGridFilterModel}})
  }

  transformFilters(key: ExtendedTableTab, formValue: any): TransformedActiveFiltersInterface[] {
    const result: TransformedActiveFiltersInterface[] = [];
    for(const filterName in formValue) {
      let selectedOptions = []
      if(typeof formValue[filterName] === 'object') {
        selectedOptions = formValue[filterName].options
          .filter((opt: {selected: boolean}) => opt.selected)
          .map((opt: {id: number}) => opt.id)
      }
      if(selectedOptions.length > 0) {
        result.push({
          name: filterName,
          values: selectedOptions,
          filter: this.getFilters(key).find(f => f.field == filterName)?.filter,
          filterParams: this.getFilters(key).find(f => f.field == filterName)?.filterParams
        })
      }
    }
    return result;
  }

  getGridFilters(): AgGridFilterModelInterface {
    const agGridFilterModel: AgGridFilterModelInterface = {};
    for(const field in this.activeFilters) {
      const values = this.activeFilters[field].values;

      if(values && values.length) {
      const stringifiedValues = values.map(v => (v !== null && v !== undefined ? String(v) : null));
        switch(this.activeFilters[field].filter) {
          case 'agSetColumnFilter':
            agGridFilterModel[this.activeFilters[field].name] = {
              values: stringifiedValues,
              filterType: 'set'
            }
            break;
          case 'agMultiColumnFilter':
            if(this.activeFilters[field].filterParams?.filters) {
              const setFilterIndex = this.activeFilters[field].filterParams.filters.findIndex((def: { filter: string; }) => def.filter == 'agSetColumnFilter');

              if(setFilterIndex != -1) {
                let filterModels = [];
                this.activeFilters[field].filterParams.filters.forEach(() => filterModels.push(null));
                filterModels[setFilterIndex] = { values: stringifiedValues, filterType: 'set' };
                agGridFilterModel[this.activeFilters[field].name] = {
                  filterModels: filterModels,
                  filterType: 'multi'
                }
              }
            }
            break;
          default:
            break;
        }
      }
    }
    return agGridFilterModel;
  }

  updateSelectValueOnTable(activeFilters: any) {
    this.itemDataCatalogService.updateFilteredDataToCatalog("categories", this.categories ?? [])
    this.itemDataCatalogService.updateFilteredDataToCatalog("departments", this.departments ?? [])

    if(activeFilters) {
      for(let filterName in activeFilters) {
        const setFilter = getFilterValues(activeFilters, filterName);
        switch (filterName) {
          case "category_name":
            let categoryValues: Categories[] = [];
            if(setFilter) {
              setFilter.forEach((value: any) => {
                const filterValue = this.categories?.find(v => v.id == parseInt(value) || (value == null && v.id == 0));
                if(filterValue) categoryValues.push( filterValue );
              })
            }
            this.itemDataCatalogService.updateFilteredDataToCatalog("categories", categoryValues)
            break;
          case "department_name":
              let departmentValues: Departments[] = [];
              if(setFilter) {
                setFilter.forEach((value: any) => {
                  const filterValue = this.departments?.find(v => v.id == parseInt(value));
                  if(filterValue) departmentValues.push( filterValue );
                })
              }
            this.itemDataCatalogService.updateFilteredDataToCatalog("departments", departmentValues)
            break;

          default:
            break;
        }
      }
    }
  }

  ngOnDestroy(): void {
    // this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
  }
}

export function getFilterValues(filter: any, filterName: string) {
  const filterGroup = filter[filterName];
  let setFilter = null;
  if(filterGroup?.filterType === 'multi') {
    const setFilterTmp = filterGroup.filterModels.filter((f: { filterType: string; }) => f?.filterType === 'set');
    if(setFilterTmp?.length) setFilter = setFilterTmp[0].values;
  } else if(filterGroup?.filterType === 'set') {
    setFilter = filterGroup.values;
  }
  return setFilter;
}
